import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, ExternalLink } from "lucide-react";
import { PRODUCTS } from "@/data/catalog";
import { API, articleImage } from "@/components/JournalParts";

const btn = "px-4 py-2 border text-[11px] uppercase tracking-[0.15em] rounded-sm transition-colors disabled:opacity-40";
const field = "w-full bg-transparent border border-[#303B2D] rounded-sm px-3 py-2 text-[14px] text-[#E8DDC7] focus:outline-none focus:border-[#B5A642]/70";
const errText = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Something went wrong.");
const STATUS = { drafting: "text-[#C9A96B]", draft: "text-[#D3CEC4]/60", published: "text-[#8FB08A]", failed: "text-[#C9876B]" };

const ProductSelect = ({ value, onChange, testid, allowAll }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={`${field} bg-[#1C1F1E]`}>
    {allowAll && <option value="">All preparations</option>}
    {PRODUCTS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
  </select>
);

const NewArticle = ({ auth, onCreated }) => {
  const [pid, setPid] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [topic, setTopic] = useState("");
  const [topicPid, setTopicPid] = useState(PRODUCTS[0].id);
  const [error, setError] = useState("");

  const suggest = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/keeper/journal/suggest`, { product_id: pid || null }, auth);
      setIdeas(data);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const draft = async (t, p) => {
    setError("");
    try {
      await axios.post(`${API}/keeper/journal/draft`, { topic: t, product_id: p }, auth);
      setIdeas((xs) => xs.filter((x) => x.topic !== t));
      setTopic("");
      onCreated();
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-8">
      <div className="border border-[#303B2D] rounded-sm p-5">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]">Ask for ideas</p>
        <div className="mt-3 flex gap-2">
          <ProductSelect value={pid} onChange={setPid} testid="journal-suggest-product" allowAll />
          <button data-testid="journal-suggest" onClick={suggest} disabled={busy} className={`${btn} border-[#B5A642]/60 text-[#D3CEC4] hover:bg-[#B5A642]/10 shrink-0 inline-flex items-center gap-2`}>
            <Sparkles className="w-3.5 h-3.5" /> {busy ? "Thinking…" : "Suggest"}
          </button>
        </div>
        <ul className="mt-4 space-y-3" data-testid="journal-ideas">
          {ideas.map((x, i) => (
            <li key={x.topic} className="border-t border-[#303B2D]/60 pt-3" data-testid={`journal-idea-${i}`}>
              <p className="font-display text-lg text-[#E8DDC7] leading-snug">{x.topic}</p>
              <p className="text-[12px] text-[#D3CEC4]/50">“{x.keyword}” · {PRODUCTS.find((p) => p.id === x.product_id)?.name}</p>
              <p className="text-[12px] text-[#D3CEC4]/45 italic">{x.why}</p>
              <button data-testid={`journal-idea-draft-${i}`} onClick={() => draft(x.topic, x.product_id)} className="mt-2 text-[11px] uppercase tracking-[0.2em] text-[#B5A642] hover:text-[#E8DDC7]">Draft this</button>
            </li>
          ))}
        </ul>
      </div>
      <div className="border border-[#303B2D] rounded-sm p-5">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#B5A642]">Or give your own topic</p>
        <input data-testid="journal-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Turkey tail for dogs who live outdoors" className={`${field} mt-3`} />
        <div className="mt-2 flex gap-2">
          <ProductSelect value={topicPid} onChange={setTopicPid} testid="journal-topic-product" />
          <button data-testid="journal-draft" disabled={topic.trim().length < 5} onClick={() => draft(topic.trim(), topicPid)} className={`${btn} border-[#B5A642]/60 text-[#D3CEC4] hover:bg-[#B5A642]/10 shrink-0`}>Draft it</button>
        </div>
        <p className="mt-3 text-[11px] text-[#D3CEC4]/40">Drafts take about a minute, in English and French, with an engraved header image. Nothing goes live until you publish it.</p>
        {error && <p data-testid="journal-error" className="mt-3 text-[12px] text-[#C9876B]">{error}</p>}
      </div>
    </div>
  );
};

const LangFields = ({ value, onChange, lang }) => {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <input data-testid={`journal-edit-${lang}-title`} value={value.title} onChange={set("title")} className={`${field} font-display text-xl`} placeholder="Title" />
      <textarea data-testid={`journal-edit-${lang}-dek`} value={value.dek} onChange={set("dek")} rows={2} className={field} placeholder="Standfirst" />
      <input data-testid={`journal-edit-${lang}-meta`} value={value.meta_description} onChange={set("meta_description")} className={field} placeholder="Google description (155 characters)" />
      <p className="text-[10px] text-[#D3CEC4]/40">{(value.meta_description || "").length} / 155</p>
      <textarea data-testid={`journal-edit-${lang}-body`} value={value.body_md} onChange={set("body_md")} rows={22} className={`${field} font-ledger text-[13px] leading-relaxed`} />
    </div>
  );
};

const Editor = ({ a, auth, onDone }) => {
  const [en, setEn] = useState(a.en);
  const [fr, setFr] = useState(a.fr);
  const [pid, setPid] = useState(a.product_id);
  const [lang, setLang] = useState("en");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (label, fn) => {
    setBusy(label);
    setMsg("");
    try {
      await fn();
      setMsg(`${label} done.`);
      onDone(label === "Delete");
    } catch (e) {
      setMsg(errText(e));
    } finally {
      setBusy("");
    }
  };
  const save = () => axios.put(`${API}/keeper/journal/${a.id}`, { en, fr, product_id: pid }, auth);
  const status = (s) => run(s === "published" ? "Publish" : s === "deleted" ? "Delete" : "Unpublish", async () => {
    if (s === "published") await save();
    await axios.post(`${API}/keeper/journal/${a.id}/status`, { status: s }, auth);
  });

  return (
    <div className="mt-6 border border-[#B5A642]/30 rounded-sm p-5" data-testid="journal-editor">
      <div className="grid md:grid-cols-3 gap-5">
        <div>
          <img src={`${articleImage(a)}${a.image_path ? `?v=${encodeURIComponent(a.image_path)}` : ""}`} alt="" className="w-full aspect-[16/9] object-cover rounded-sm border border-[#303B2D]" data-testid="journal-editor-image" />
          <button data-testid="journal-regen-image" disabled={!!busy} onClick={() => run("New image", () => axios.post(`${API}/keeper/journal/${a.id}/image`, {}, auth))}
            className={`${btn} mt-2 w-full border-[#303B2D] text-[#D3CEC4]/70 hover:border-[#B5A642]/60 inline-flex items-center justify-center gap-2`}>
            <RefreshCw className="w-3.5 h-3.5" /> {busy === "New image" ? "Engraving…" : "New image"}
          </button>
          <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[#D3CEC4]/45">Links to</p>
          <div className="mt-1"><ProductSelect value={pid} onChange={setPid} testid="journal-edit-product" /></div>
        </div>
        <div className="md:col-span-2">
          <div className="flex gap-6 mb-4">
            {["en", "fr"].map((l) => (
              <button key={l} data-testid={`journal-edit-tab-${l}`} onClick={() => setLang(l)} className={`text-[11px] uppercase tracking-[0.25em] pb-1 border-b ${lang === l ? "text-[#B5A642] border-[#B5A642]" : "text-[#D3CEC4]/50 border-transparent"}`}>
                {l === "en" ? "English" : "Français"}
              </button>
            ))}
          </div>
          {lang === "en" ? <LangFields value={en} onChange={setEn} lang="en" /> : <LangFields value={fr} onChange={setFr} lang="fr" />}
        </div>
      </div>
      <p className="mt-4 text-[11px] text-[#C9A96B]/80">Before publishing, check: only "supports / helps maintain" wording, no named diseases, no invented studies.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid="journal-save" disabled={!!busy} onClick={() => run("Save", save)} className={`${btn} border-[#B5A642]/60 text-[#D3CEC4] hover:bg-[#B5A642]/10`}>Save</button>
        {a.status !== "published" ? (
          <button data-testid="journal-publish" disabled={!!busy} onClick={() => status("published")} className={`${btn} border-[#8FB08A]/60 text-[#8FB08A] hover:bg-[#8FB08A]/10`}>Save &amp; publish</button>
        ) : (
          <button data-testid="journal-unpublish" disabled={!!busy} onClick={() => status("draft")} className={`${btn} border-[#303B2D] text-[#D3CEC4]/70`}>Unpublish</button>
        )}
        {a.status === "published" && (
          <a href={`/journal/${a.slug}`} target="_blank" rel="noreferrer" data-testid="journal-view-live" className={`${btn} border-[#303B2D] text-[#D3CEC4]/70 inline-flex items-center gap-2`}><ExternalLink className="w-3.5 h-3.5" /> View live</a>
        )}
        <button data-testid="journal-delete" disabled={!!busy} onClick={() => window.confirm("Delete this article?") && status("deleted")} className={`${btn} ml-auto border-[#303B2D] text-[#C9876B]/80 hover:border-[#C9876B]/60`}>Delete</button>
      </div>
      {msg && <p data-testid="journal-editor-msg" className="mt-3 text-[12px] text-[#D3CEC4]/60">{msg}</p>}
    </div>
  );
};

export const KeeperJournal = ({ token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => axios.get(`${API}/keeper/journal`, auth).then((r) => setArticles(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const drafting = articles.some((a) => a.status === "drafting");
  useEffect(() => {
    if (!drafting) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafting]);

  return (
    <div data-testid="keeper-journal">
      <NewArticle auth={auth} onCreated={load} />
      <ul className="mt-10">
        {articles.length === 0 && <p data-testid="journal-keeper-empty" className="font-display italic text-[#D3CEC4]/50">No articles yet. Ask for ideas above.</p>}
        {articles.map((a) => (
          <li key={a.id} className="py-4 border-b border-[#303B2D]/60" data-testid={`journal-row-${a.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`text-[10px] uppercase tracking-[0.2em] ${STATUS[a.status]}`} data-testid={`journal-row-status-${a.id}`}>{a.status === "drafting" ? "drafting…" : a.status}</span>
              <span className="font-display text-lg text-[#E8DDC7]">{a.en?.title || a.topic}</span>
              <span className="text-[12px] text-[#D3CEC4]/40">{PRODUCTS.find((p) => p.id === a.product_id)?.name}</span>
              {["draft", "published"].includes(a.status) && (
                <button data-testid={`journal-row-edit-${a.id}`} onClick={() => setOpen(open === a.id ? null : a.id)} className="ml-auto text-[11px] uppercase tracking-[0.2em] text-[#B5A642] hover:text-[#E8DDC7]">
                  {open === a.id ? "Close" : "Review"}
                </button>
              )}
              {a.status === "failed" && <span className="ml-auto text-[12px] text-[#C9876B]">The draft failed. Try the topic again.</span>}
            </div>
            {open === a.id && <Editor key={`${a.id}-${a.image_path}`} a={a} auth={auth} onDone={(closed) => { if (closed) setOpen(null); load(); }} />}
          </li>
        ))}
      </ul>
    </div>
  );
};
